import {
  loadState,
  setGameType,
  setPlayers,
  setPolicyMarket,
  startGame,
  tutorialStartChapter
} from '../../redux/actions';
import {GAME_TYPES} from '../../constants/game';
import {getTutorialChapterGroundBackground} from '../../configs/tutorial';
import {PLAYER_BONUS_TYPES} from '../../configs/playerBonus';
import {FINAL_ROUND} from '../../configs/game';
import {getNewPolicyMarket} from '../policy/getNewPolicyMarket';
import {getTutorialRunKind, TUTORIAL_ANALYTICS_EVENTS} from './tutorialAnalyticsIds';

/**
 * Старт главы обучения на уже загруженном поле главы: фиксированные цвета
 * (совпадают с полем tutorial*.json), нулевой сдвиг инициативы (игрок ходит
 * первым), старт сценария.
 *
 * Порядок важен для записи прохождения: запись начинается ДО первого экшена
 * главы — иначе в ней не окажется поля, и смотреть ее будет не на чем. Та же
 * функция стартует главу и в headless-пробнике обучения, поэтому проверка
 * воспроизведения записи (tutorialReplayDeterminism.e2e.js) проверяет ровно
 * этот порядок.
 *
 * Сеть и аналитика передаются снаружи: пробник работает без браузера, а SDK
 * аналитики в Node не загружается.
 *
 * @param {Object} params
 * @param {Function} params.dispatch
 * @param {Object} params.chapter - конфиг главы
 * @param {Object} params.fieldData - содержимое chapter.fieldPath
 * @param {Object|null} [params.recorder] - рекордер записи прохождения
 * @param {Function} [params.sendEvent] - sendTutorialEvent
 * @param {Function} [params.isMounted] - партия еще на экране (поле грузится
 *   асинхронно, игрок мог уйти в меню раньше)
 * @param {string|null} [params.playerName] - имя из профиля
 * @param {string[]} [params.completedChapterIds] - пройденные главы
 * @param {number} [params.timestamp] - серверное время старта
 * @returns {boolean} глава стартовала
 */
export const bootstrapTutorialChapter = ({
  dispatch,
  chapter,
  fieldData,
  recorder = null,
  sendEvent = () => {},
  isMounted = () => true,
  playerName = null,
  completedChapterIds = [],
  timestamp = Date.now(),
}) => {
  if (!isMounted()) {
    return false;
  }

  // Глава уже была пройдена раньше — повторное прохождение (признак метрик,
  // фиксируется на старте)
  const isRepeatRun = (completedChapterIds || []).includes(chapter.id);

  // Запись прохождения — с самого первого экшена главы: запись в истории
  // матчей создается на старте, а не в конце
  recorder?.beginRun(chapter.id);
  dispatch(setGameType(GAME_TYPES.LOCAL));
  // Подложка у главы постоянная (см. groundBackground в конфиге главы).
  // Кладем ее уже в LOAD_STATE: поле может смонтироваться сразу после
  // загрузки, а сцена читает подложку один раз при старте
  const groundBackground = getTutorialChapterGroundBackground(chapter);

  if (fieldData.gameState) {
    // Снапшотные главы (2-3): файл содержит полный gameState (игроки, отряды,
    // раунд, фаза, рынок политик) — LOAD_STATE восстанавливает партию целиком,
    // setPlayers/startGame не нужны и затерли бы восстановленное состояние.
    // Имя живого игрока берем из профиля
    const patchedGameState = {
      ...fieldData.gameState,
      players: (fieldData.gameState.players || []).map((player) => (player.isBot
        // Отряды противника в обучении рисуются фракционными иконками (см.
        // useFactionUnitSkin в HexGridRenderer.renderUnit): глава заодно
        // показывает, как выглядит войско этой фракции
        ? {...player, hasUnitSkin: true}
        : {...player, name: playerName || player.name})),
    };
    dispatch(loadState(
      {...fieldData, gameState: patchedGameState, timerEnabled: false, groundBackground},
      chapter.finalRound || FINAL_ROUND
    ));
  } else {
    const tutorialPlayers = [
      {
        id: 'player_id_blue',
        color: 'blue',
        name: playerName || 'Игрок',
        isBot: false,
        factionId: chapter.playerFaction,
        bonus: PLAYER_BONUS_TYPES.NONE,
        // Бюджет главы на свои шаги сверх обычного старта (глава 1 — вторая ферма)
        ...(chapter.playerExtraStartingCoins > 0 ? {extraStartingCoins: chapter.playerExtraStartingCoins} : {}),
      },
      {
        id: 'player_id_red',
        color: 'red',
        name: chapter.botName,
        isBot: true,
        factionId: chapter.botFaction,
        bonus: PLAYER_BONUS_TYPES.NONE,
        // Отряды противника — фракционными иконками, как и в снапшотных главах
        hasUnitSkin: true,
      },
    ];
    dispatch(loadState({...fieldData, timerEnabled: false, groundBackground}, chapter.finalRound || FINAL_ROUND));
    dispatch(setPlayers(tutorialPlayers));
    dispatch(setPolicyMarket(chapter.policyMarket || getNewPolicyMarket()));
    dispatch(startGame(timestamp, false, false, 0, false, groundBackground));
  }

  dispatch(tutorialStartChapter(chapter.id, {isRepeatRun}));
  sendEvent(chapter.id, TUTORIAL_ANALYTICS_EVENTS.START, getTutorialRunKind(isRepeatRun));
  return true;
};
